/**
 * BibleStudyShowcase — Premium renderer for bible_study layout.
 *
 * Identity: spiritual, serene, contemplative.
 * Palette: deep navy + purple accent + gold verse highlight.
 * Desktop: Hero → 2-col (StructureMap + ScriptureCard+Tip) → Sections → Chips + optional sidebar
 * Mobile: stacked, seções expansíveis, leitura confortável
 */

import React, { useState } from 'react';
import { usePresentationBreakpoint, AthenaTip, ActionChips }
  from '../presentation-system';
import { ProseText } from './ProseText';

// ── Palette ──────────────────────────────────────────────────────────────────
const P = {
  card:       '#0d1325',
  border:     '#1e2a44',
  borderS:    'rgba(30,42,68,0.5)',
  purple:     '#a78bfa',
  purpleSoft: 'rgba(167,139,250,0.11)',
  purpleBdr:  'rgba(167,139,250,0.22)',
  purpleL:    '#c4b5fd',
  gold:       '#f59e0b',
  goldSoft:   'rgba(245,158,11,0.09)',
  goldBdr:    'rgba(245,158,11,0.27)',
  goldL:      '#fbbf24',
  blue:       '#60a5fa',
  blueSoft:   'rgba(96,165,250,0.09)',
  blueBdr:    'rgba(96,165,250,0.22)',
  green:      '#22c55e',
  greenSoft:  'rgba(34,197,94,0.09)',
  greenBdr:   'rgba(34,197,94,0.22)',
  text:       '#e8eaf2',
  textSec:    'rgba(220,230,255,0.78)',
  textMut:    'rgba(150,170,220,0.55)',
  heroGrad:   'linear-gradient(135deg, #090d1c 0%, #0b1228 100%)',
  verseGrad:  'linear-gradient(135deg, rgba(245,158,11,0.11) 0%, rgba(245,158,11,0.04) 100%)',
  tipGrad:    'linear-gradient(135deg, rgba(167,139,250,0.13) 0%, rgba(167,139,250,0.05) 100%)',
  msgGrad:    'linear-gradient(135deg, rgba(167,139,250,0.10) 0%, rgba(99,102,241,0.06) 100%)',
};

// ── Bible Hero Photo (left panel — 42% width, split composition) ─────────────
function BibleHeroPhoto({ imgUrl, title }) {
  return (
    <div style={{
      flex: '0 0 42%', height: '100%',
      position: 'relative', overflow: 'hidden',
      background: 'linear-gradient(135deg, rgba(99,102,241,0.18), rgba(167,139,250,0.12))',
    }}>
      {imgUrl ? (
        <img src={imgUrl} alt={title}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
      ) : (
        <div style={{
          width: '100%', height: '100%',
          background: 'linear-gradient(135deg, #090e1f 0%, #131a35 50%, #0a0d1c 100%)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          gap: 8,
        }}>
          <div style={{ fontSize: 36, opacity: 0.45 }}>✝</div>
          <div style={{ fontSize: 10, color: 'rgba(167,139,250,0.35)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Bíblia</div>
        </div>
      )}
      {imgUrl && (
        <div style={{
          position: 'absolute', top: 0, right: 0, bottom: 0, width: '35%',
          background: `linear-gradient(to right, transparent, ${P.card})`,
          pointerEvents: 'none',
        }} />
      )}
      <div style={{
        position: 'absolute', top: 10, left: 12, zIndex: 3,
        padding: '4px 12px', borderRadius: 20,
        background: imgUrl ? 'rgba(167,139,250,0.9)' : 'rgba(167,139,250,0.15)',
        border: `1px solid ${P.purpleBdr}`,
        fontSize: 10, fontWeight: 800, letterSpacing: '0.12em',
        color: imgUrl ? '#fff' : P.purpleL,
        textTransform: 'uppercase',
        backdropFilter: imgUrl ? 'blur(8px)' : 'none',
        boxShadow: imgUrl ? '0 2px 12px rgba(139,92,246,0.3)' : 'none',
      }}>✝ BÍBLIA</div>
    </div>
  );
}

// ── Metric chip (hover interactive) ──────────────────────────────────────────
function MetricChip({ icon, value, label }) {
  const [hov, setHov] = useState(false);
  if (!value) return null;
  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center',
        padding: '10px 14px', borderRadius: 12, gap: 4,
        background: hov ? 'rgba(167,139,250,0.08)' : 'rgba(255,255,255,0.03)',
        border: `1px solid ${hov ? P.purpleBdr : P.border}`,
        cursor: 'default', transition: 'all 0.18s ease',
        boxShadow: hov ? '0 0 14px rgba(167,139,250,0.08)' : 'none',
      }}
    >
      <div style={{ fontSize: 16, lineHeight: 1 }}>{icon}</div>
      <div style={{ fontSize: 14, fontWeight: 700, color: P.text, letterSpacing: '-0.01em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>{value}</div>
      <div style={{ fontSize: 10, color: P.textMut, textTransform: 'uppercase', letterSpacing: '0.07em', whiteSpace: 'nowrap' }}>{label}</div>
    </div>
  );
}

// ── Hero (desktop) — split panel: foto 42% esquerda + texto direita ──────────
function BibleHeroDesktop({ d }) {
  const depthMap = { básico: 'Introdutório', médio: 'Intermediário', profundo: 'Avançado' };
  const depthLabel = depthMap[d.depth_level] || d.depth_level;
  const secCount   = d.section_count || (d.sections || []).length || null;
  const readMin    = d.depth_level === 'profundo' ? '~30 min' : d.depth_level === 'médio' ? '~20 min' : '~15 min';
  const imgUrl     = d.hero_image?.desktop_url || null;
  const titleFs    = (d.title || '').length > 38 ? 21 : (d.title || '').length > 24 ? 26 : 30;

  return (
    <div style={{
      background: 'linear-gradient(135deg, #0b1524 0%, #0d1a2e 100%)',
      border: `1px solid ${P.border}`,
      borderRadius: 14, overflow: 'hidden', display: 'flex', height: 260,
      boxShadow: '0 4px 40px rgba(0,0,0,0.4), 0 1px 0 rgba(255,255,255,0.03) inset',
    }}>
      <BibleHeroPhoto imgUrl={imgUrl} title={d.title} />

      <div style={{
        flex: 1, padding: '20px 24px',
        display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
        minWidth: 0,
      }}>
        <div>
          {d.reference && (
            <div style={{ fontSize: 12.5, fontWeight: 700, color: P.gold, letterSpacing: '0.02em', marginBottom: 6 }}>
              {d.reference}{d.translation ? ` · ${d.translation}` : ''}
            </div>
          )}
          <div style={{
            fontSize: titleFs, fontWeight: 800, color: P.text,
            lineHeight: 1.2, letterSpacing: '-0.02em',
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>
            {d.title}
          </div>
          {d.subtitle && d.subtitle !== d.title && (
            <div style={{
              fontSize: 13, color: P.textSec, lineHeight: 1.5, marginTop: 6, fontStyle: 'italic',
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
            }}>
              {d.subtitle}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <MetricChip icon="⏱" value={readMin} label="Duração" />
          {depthLabel && <MetricChip icon="📊" value={depthLabel} label="Nível" />}
          {secCount ? <MetricChip icon="🗺" value={`${secCount} tópicos`} label="Conteúdo" /> : null}
          {d.literary_context && <MetricChip icon="📝" value={d.literary_context} label="Gênero" />}
        </div>
      </div>
    </div>
  );
}

// ── Hero (mobile) — texto esquerda + foto direita + metrics strip ─────────────
function BibleHeroMobile({ d }) {
  const depthMap = { básico: 'Básico', médio: 'Médio', profundo: 'Profundo' };
  const imgUrl   = d.hero_image?.mobile_url || d.hero_image?.desktop_url || null;
  const titleFs  = (d.title || '').length > 32 ? 18 : (d.title || '').length > 22 ? 21 : 24;
  const readMin  = d.depth_level === 'profundo' ? '~30 min' : d.depth_level === 'médio' ? '~20 min' : '~15 min';
  const METRICS  = [
    { icon: '⏱', value: readMin,                                    label: 'Duração'  },
    { icon: '📊', value: depthMap[d.depth_level] || d.depth_level,  label: 'Nível'    },
    d.literary_context && { icon: '📝', value: d.literary_context,  label: 'Gênero'   },
    d.reference        && { icon: '📖', value: d.reference,         label: 'Passagem' },
  ].filter(Boolean).slice(0, 4);

  return (
    <div style={{
      background: 'linear-gradient(135deg, #0b1524 0%, #0d1a2e 100%)',
      border: `1px solid ${P.border}`,
      borderRadius: 12, overflow: 'hidden',
      boxShadow: '0 4px 40px rgba(0,0,0,0.4)',
    }}>
      <div style={{ display: 'flex', minHeight: 202, position: 'relative' }}>
        <div style={{
          flex: 1, minWidth: 0, padding: '20px 10px 18px 18px',
          display: 'flex', flexDirection: 'column', justifyContent: 'flex-start',
        }}>
          <div style={{
            display: 'inline-block', alignSelf: 'flex-start',
            padding: '3px 10px', borderRadius: 20, marginBottom: 12,
            background: P.purpleSoft, border: `1px solid ${P.purpleBdr}`,
            fontSize: 10, fontWeight: 800, letterSpacing: '0.12em',
            color: P.purpleL, textTransform: 'uppercase',
          }}>✝ BÍBLIA</div>
          {d.reference && (
            <div style={{ fontSize: 11.5, fontWeight: 700, color: P.gold, marginBottom: 4 }}>
              {d.reference}{d.translation ? ` · ${d.translation}` : ''}
            </div>
          )}
          <div style={{ fontSize: titleFs, fontWeight: 800, color: P.text, lineHeight: 1.24, letterSpacing: '-0.025em' }}>
            {d.title}
          </div>
          {d.subtitle && d.subtitle !== d.title && (
            <div style={{
              fontSize: 12, color: 'rgba(220,214,255,0.62)', lineHeight: 1.55, marginTop: 8,
              display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden',
            }}>
              {d.subtitle}
            </div>
          )}
        </div>

        <div style={{ flexShrink: 0, width: '42%', position: 'relative', overflow: 'hidden' }}>
          {imgUrl ? (
            <>
              <img src={imgUrl} alt={d.title}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              <div style={{
                position: 'absolute', top: 0, left: 0, bottom: 0, width: '55%',
                background: `linear-gradient(to right, ${P.card} 0%, rgba(13,19,37,0.7) 50%, transparent 100%)`,
                pointerEvents: 'none',
              }} />
            </>
          ) : (
            <div style={{
              width: '100%', height: '100%', minHeight: 202,
              background: 'linear-gradient(135deg, #090e1f 0%, #131a35 55%, #0a0d1c 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <div style={{ fontSize: 30, opacity: 0.45 }}>✝</div>
            </div>
          )}
        </div>
      </div>

      <div style={{ borderTop: `1px solid ${P.border}`, display: 'flex', background: 'rgba(0,0,0,0.18)' }}>
        {METRICS.map((m, i) => (
          <div key={i} style={{
            flex: 1, minWidth: 0,
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            gap: 3, padding: '12px 4px',
            borderRight: i < METRICS.length - 1 ? `1px solid ${P.border}` : 'none',
          }}>
            <div style={{ fontSize: 15, lineHeight: 1 }}>{m.icon}</div>
            <div style={{ fontSize: 12, fontWeight: 700, color: P.text, lineHeight: 1.2, textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>{m.value}</div>
            <div style={{ fontSize: 9, color: P.textMut, textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'center', lineHeight: 1.2 }}>{m.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Scripture card ────────────────────────────────────────────────────────────
function ScriptureCard({ passage, reference, translation }) {
  if (!passage) return null;
  return (
    <div style={{
      background: P.verseGrad,
      border: `1px solid ${P.goldBdr}`,
      borderLeft: `3px solid ${P.gold}`,
      borderRadius: 10, padding: '12px 14px',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 5, marginBottom: 9,
        fontSize: 9, fontWeight: 800, color: P.gold,
        textTransform: 'uppercase', letterSpacing: '0.09em',
      }}>
        📜 VERSÍCULO-CHAVE
      </div>
      <ProseText
        text={`"${passage}"`}
        style={{ fontSize: 13.5, color: P.text, fontStyle: 'italic', lineHeight: 1.72, whiteSpace: 'pre-wrap' }}
      />
      {(reference || translation) && (
        <div style={{
          marginTop: 8, fontSize: 11, color: P.gold, fontWeight: 600,
          textAlign: 'right',
        }}>
          — {reference}{translation ? ` (${translation})` : ''}
        </div>
      )}
    </div>
  );
}

// ── Structure map ─────────────────────────────────────────────────────────────
function StructureMap({ items }) {
  if (!items || items.length === 0) return null;
  return (
    <div style={{
      background: P.card, border: `1px solid ${P.border}`,
      borderRadius: 10, padding: '10px 12px', height: '100%',
    }}>
      <div style={{
        fontSize: 9, fontWeight: 800, letterSpacing: '0.09em',
        textTransform: 'uppercase', color: P.textMut, marginBottom: 9,
      }}>
        🗺 ESTRUTURA DO ESTUDO
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {items.map((item, i) => (
          <div key={i} style={{
            display: 'flex', alignItems: 'flex-start', gap: 8,
            fontSize: 12, color: P.textSec,
          }}>
            <div style={{
              width: 20, height: 20, borderRadius: '50%',
              background: 'rgba(167,139,250,0.72)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 9, fontWeight: 800, color: '#fff',
              flexShrink: 0, marginTop: 1,
            }}>
              {i + 1}
            </div>
            <span style={{ lineHeight: 1.4, paddingTop: 2 }}>{item}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Context card ─────────────────────────────────────────────────────────────
function ContextCard({ context, author, audience, literaryContext }) {
  const hasAny = context || author || audience || literaryContext;
  if (!hasAny) return null;
  return (
    <div style={{
      background: P.card, border: `1px solid ${P.border}`,
      borderLeft: `3px solid ${P.blue}`,
      borderRadius: 10, padding: '12px 14px',
    }}>
      <div style={{
        fontSize: 9, fontWeight: 800, letterSpacing: '0.09em',
        textTransform: 'uppercase', color: 'rgba(96,165,250,0.7)',
        marginBottom: 9, display: 'flex', alignItems: 'center', gap: 5,
      }}>
        🏛 CONTEXTO HISTÓRICO
      </div>
      {context && (
        <div style={{ marginBottom: (author || audience || literaryContext) ? 10 : 0 }}>
          <ProseText
            text={context}
            style={{ fontSize: 13, color: P.textSec, lineHeight: 1.65, whiteSpace: 'pre-wrap' }}
          />
        </div>
      )}
      {(author || audience || literaryContext) && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {author && (
            <div style={{
              padding: '3px 10px', borderRadius: 20,
              background: P.blueSoft, border: `1px solid ${P.blueBdr}`,
              fontSize: 11, color: P.blue,
            }}>
              ✍ {author}
            </div>
          )}
          {audience && (
            <div style={{
              padding: '3px 10px', borderRadius: 20,
              background: P.greenSoft, border: `1px solid ${P.greenBdr}`,
              fontSize: 11, color: P.green,
            }}>
              👥 {audience}
            </div>
          )}
          {literaryContext && (
            <div style={{
              padding: '3px 10px', borderRadius: 20,
              background: P.purpleSoft, border: `1px solid ${P.purpleBdr}`,
              fontSize: 11, color: P.purpleL,
            }}>
              📝 {literaryContext}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Generic section card ──────────────────────────────────────────────────────
function SectionCard({ title, content, icon, accentColor, accentBdr }) {
  if (!content) return null;
  const ac = accentColor || P.purple;
  return (
    <div style={{
      background: P.card, border: `1px solid ${P.border}`,
      borderLeft: `3px solid ${ac}`,
      borderRadius: 10, padding: '12px 14px',
    }}>
      <div style={{
        fontSize: 9, fontWeight: 800, letterSpacing: '0.09em',
        textTransform: 'uppercase', color: `${ac}bb`,
        marginBottom: 8, display: 'flex', alignItems: 'center', gap: 5,
      }}>
        {icon} {title}
      </div>
      <ProseText text={content} style={{ fontSize: 13, color: P.textSec, lineHeight: 1.65, whiteSpace: 'pre-wrap' }} />
    </div>
  );
}

// ── Central message ───────────────────────────────────────────────────────────
function CentralMessage({ message }) {
  if (!message) return null;
  return (
    <div style={{
      background: P.msgGrad,
      border: `1px solid ${P.purpleBdr}`,
      borderRadius: 12, padding: '14px 16px',
      textAlign: 'center',
    }}>
      <div style={{
        fontSize: 9, fontWeight: 800, letterSpacing: '0.10em',
        textTransform: 'uppercase', color: P.purple, marginBottom: 8,
      }}>
        ✨ MENSAGEM CENTRAL
      </div>
      <ProseText
        text={message}
        style={{ fontSize: 14.5, fontWeight: 600, color: P.text, lineHeight: 1.55, whiteSpace: 'pre-wrap', fontStyle: 'italic' }}
      />
    </div>
  );
}

// ── Reflection prompt ─────────────────────────────────────────────────────────
function ReflectionPrompt({ question }) {
  if (!question) return null;
  return (
    <div style={{
      background: P.card, border: `1px solid ${P.border}`,
      borderRadius: 10, padding: '12px 14px',
      display: 'flex', alignItems: 'flex-start', gap: 12,
    }}>
      <div style={{
        width: 32, height: 32, borderRadius: '50%',
        background: P.goldSoft, border: `1px solid ${P.goldBdr}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 16, flexShrink: 0,
      }}>
        💭
      </div>
      <div>
        <div style={{
          fontSize: 9, fontWeight: 800, letterSpacing: '0.09em',
          textTransform: 'uppercase', color: 'rgba(245,158,11,0.7)',
          marginBottom: 5,
        }}>
          PERGUNTA DE REFLEXÃO
        </div>
        <div style={{
          fontSize: 14, color: P.text, lineHeight: 1.55, fontStyle: 'italic',
        }}>
          {question}
        </div>
      </div>
    </div>
  );
}

// ── Prayer card ───────────────────────────────────────────────────────────────
function PrayerCard({ prayer }) {
  if (!prayer) return null;
  return (
    <div style={{
      background: P.tipGrad,
      border: `1px solid ${P.purpleBdr}`,
      borderRadius: 10, padding: '12px 14px',
    }}>
      <div style={{
        fontSize: 9, fontWeight: 800, letterSpacing: '0.09em',
        textTransform: 'uppercase', color: P.purple, marginBottom: 8,
        display: 'flex', alignItems: 'center', gap: 5,
      }}>
        🙏 ORAÇÃO
      </div>
      <ProseText
        text={prayer}
        style={{ fontSize: 13, color: 'rgba(221,214,254,0.88)', lineHeight: 1.72, whiteSpace: 'pre-wrap', fontStyle: 'italic' }}
      />
    </div>
  );
}

// ── Connection grid ───────────────────────────────────────────────────────────
function ConnectionGrid({ passages }) {
  if (!passages || passages.length === 0) return null;
  return (
    <div style={{
      background: P.card, border: `1px solid ${P.border}`,
      borderRadius: 10, padding: '10px 14px',
    }}>
      <div style={{
        fontSize: 9, fontWeight: 800, letterSpacing: '0.09em',
        textTransform: 'uppercase', color: P.textMut, marginBottom: 8,
      }}>
        🔗 PASSAGENS RELACIONADAS
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {passages.map((ref, i) => (
          <div key={i} style={{
            padding: '4px 10px', borderRadius: 20,
            background: P.goldSoft, border: `1px solid ${P.goldBdr}`,
            fontSize: 11, fontWeight: 600, color: P.goldL,
          }}>
            📖 {ref}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Key words ─────────────────────────────────────────────────────────────────
function KeyWords({ words }) {
  if (!words || words.length === 0) return null;
  return (
    <div style={{
      background: P.card, border: `1px solid ${P.border}`,
      borderRadius: 10, padding: '10px 14px',
    }}>
      <div style={{
        fontSize: 9, fontWeight: 800, letterSpacing: '0.09em',
        textTransform: 'uppercase', color: P.textMut, marginBottom: 8,
      }}>
        🔑 PALAVRAS-CHAVE
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {words.map((w, i) => (
          <span key={i} style={{
            padding: '3px 10px', borderRadius: 20,
            background: P.purpleSoft, border: `1px solid ${P.purpleBdr}`,
            fontSize: 11, fontWeight: 600, color: P.purpleL,
          }}>
            {w}
          </span>
        ))}
      </div>
    </div>
  );
}

// ── Summary card (sidebar) ────────────────────────────────────────────────────
function SummaryCard({ d }) {
  const depthMap = { básico: 'Básico', médio: 'Médio', profundo: 'Profundo' };
  const rows = [
    d.reference       && { icon: '📖', label: 'Passagem',    value: d.reference },
    d.translation     && { icon: '📋', label: 'Tradução',    value: d.translation },
    d.depth_level     && { icon: '📊', label: 'Nível',       value: depthMap[d.depth_level] || d.depth_level },
    d.literary_context && { icon: '📝', label: 'Gênero',     value: d.literary_context },
    d.author          && { icon: '✍',  label: 'Autor',       value: d.author },
  ].filter(Boolean);

  if (rows.length === 0) return null;

  return (
    <div style={{
      background: P.card, border: `1px solid ${P.border}`,
      borderRadius: 10, overflow: 'hidden',
    }}>
      <div style={{
        padding: '7px 12px', borderBottom: `1px solid ${P.border}`,
        fontSize: 9, fontWeight: 800, letterSpacing: '0.09em',
        textTransform: 'uppercase', color: P.textMut,
      }}>
        RESUMO
      </div>
      {rows.map((it, i) => (
        <div key={i} style={{
          display: 'flex', gap: 8, padding: '7px 12px',
          borderBottom: i < rows.length - 1 ? `1px solid ${P.borderS}` : 'none',
          fontSize: 12, alignItems: 'flex-start',
        }}>
          <span style={{ flexShrink: 0 }}>{it.icon}</span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
            <span style={{ fontSize: 9, color: P.textMut, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              {it.label}
            </span>
            <span style={{ color: P.textSec, fontWeight: 500, wordBreak: 'break-word' }}>
              {it.value}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Actions card (sidebar) ────────────────────────────────────────────────────
function ActionsCard({ onAction }) {
  const acts = [
    { id: 'Anotar',           icon: '📝', label: 'Anotar reflexão' },
    { id: 'Oração',           icon: '🙏', label: 'Orar com Athena' },
    { id: 'Contexto',         icon: '🏛', label: 'Ver contexto' },
    { id: 'Próximo versículo', icon: '▶', label: 'Próximo versículo' },
  ];
  return (
    <div style={{
      background: P.card, border: `1px solid ${P.border}`,
      borderRadius: 10, overflow: 'hidden',
    }}>
      <div style={{
        padding: '7px 12px', borderBottom: `1px solid ${P.border}`,
        fontSize: 9, fontWeight: 800, letterSpacing: '0.09em',
        textTransform: 'uppercase', color: P.textMut,
      }}>
        AÇÕES
      </div>
      {acts.map((a, i) => (
        <button
          key={i}
          onClick={() => onAction && onAction(`follow:${a.id}`)}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', gap: 8,
            padding: '8px 12px', background: 'transparent', border: 'none',
            borderBottom: i < acts.length - 1 ? `1px solid ${P.borderS}` : 'none',
            color: P.textSec, fontSize: 12, cursor: 'pointer', textAlign: 'left',
          }}
        >
          <span>{a.icon}</span>
          <span>{a.label}</span>
        </button>
      ))}
    </div>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────
export default function BibleStudyShowcase({ data, wp, onAction }) {
  const d = wp || data;
  const isMobile = usePresentationBreakpoint(640);
  if (!d) return null;

  // Build structure map from available content fields
  const structureItems = [];
  if (d.context)         structureItems.push('Contexto Histórico');
  if (d.observations)    structureItems.push('O Que o Texto Mostra');
  if (d.central_message) structureItems.push('Mensagem Central');
  if (d.reflection)      structureItems.push('Reflexão');
  if (d.application)     structureItems.push('Aplicação Prática');
  if (d.prayer)          structureItems.push('Oração');
  (d.sections || []).forEach(s => {
    if (!structureItems.includes(s.title)) structureItems.push(s.title);
  });

  const hasPassage    = !!d.passage;
  const hasTwoCols    = structureItems.length > 0 || hasPassage;
  const hasMainContent = !!(d.context || d.observations || d.central_message || d.reflection || d.application);
  const showSidebar   = !isMobile && (d.reference || d.literary_context || d.author || d.translation);
  const chips = d.follow_up_chips || ["Contexto", "Aplicação", "Próximo versículo", "Reflexão", "Oração", "Anotar"];

  // ── Mobile ─────────────────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}>
        <BibleHeroMobile d={d} />
        {hasPassage && (
          <ScriptureCard
            passage={d.passage}
            reference={d.reference}
            translation={d.translation}
          />
        )}
        {structureItems.length > 0 && <StructureMap items={structureItems} />}
        <ContextCard
          context={d.context}
          author={d.author}
          audience={d.audience}
          literaryContext={d.literary_context}
        />
        {d.observations && (
          <SectionCard
            icon="👁" title="O QUE O TEXTO MOSTRA"
            content={d.observations}
            accentColor={P.blue}
          />
        )}
        <CentralMessage message={d.central_message} />
        {d.reflection && (
          <SectionCard icon="📖" title="REFLEXÃO" content={d.reflection} />
        )}
        {d.application && (
          <SectionCard
            icon="🌱" title="APLICAÇÃO PRÁTICA"
            content={d.application}
            accentColor={P.green}
          />
        )}
        {(d.sections || []).map((sec, i) => (
          <SectionCard
            key={i}
            icon="•" title={sec.title.toUpperCase()}
            content={sec.content}
          />
        ))}
        <ReflectionPrompt question={d.reflection_question} />
        <PrayerCard prayer={d.prayer} />
        <ConnectionGrid passages={d.related_passages} />
        {d.key_words && d.key_words.length > 0 && <KeyWords words={d.key_words} />}
        <AthenaTip tip={d.tip} />
        <ActionChips chips={chips} onAction={onAction} accentColor={P.purple} />
      </div>
    );
  }

  // ── Desktop ────────────────────────────────────────────────────────────────
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', width: '100%' }}>

      {/* Main column */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <BibleHeroDesktop d={d} />

        {/* Two-column: StructureMap (left) + ScriptureCard & tip (right) */}
        {hasTwoCols && (
          <div style={{ display: 'flex', gap: 10, alignItems: 'stretch' }}>
            {structureItems.length > 0 && (
              <div style={{ flex: '0 0 44%' }}>
                <StructureMap items={structureItems} />
              </div>
            )}
            {hasPassage && (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 10 }}>
                <ScriptureCard
                  passage={d.passage}
                  reference={d.reference}
                  translation={d.translation}
                />
                {/* Show tip here if no main content below (avoids empty gap) */}
                {!hasMainContent && <AthenaTip tip={d.tip} />}
              </div>
            )}
          </div>
        )}

        {/* Content sections */}
        <ContextCard
          context={d.context}
          author={d.author}
          audience={d.audience}
          literaryContext={d.literary_context}
        />
        {d.observations && (
          <SectionCard
            icon="👁" title="O QUE O TEXTO MOSTRA"
            content={d.observations}
            accentColor={P.blue}
          />
        )}
        <CentralMessage message={d.central_message} />
        {d.reflection && (
          <SectionCard icon="📖" title="REFLEXÃO" content={d.reflection} />
        )}
        {d.application && (
          <SectionCard
            icon="🌱" title="APLICAÇÃO PRÁTICA"
            content={d.application}
            accentColor={P.green}
          />
        )}
        {(d.sections || []).map((sec, i) => (
          <SectionCard
            key={i}
            icon="•" title={sec.title.toUpperCase()}
            content={sec.content}
          />
        ))}

        <ReflectionPrompt question={d.reflection_question} />
        <PrayerCard prayer={d.prayer} />
        <ConnectionGrid passages={d.related_passages} />
        {d.key_words && d.key_words.length > 0 && <KeyWords words={d.key_words} />}
        {/* Main tip position: after content, before chips */}
        {hasMainContent && <AthenaTip tip={d.tip} />}
        <ActionChips chips={chips} onAction={onAction} accentColor={P.purple} />
      </div>

      {/* Optional sidebar */}
      {showSidebar && (
        <div style={{
          width: 200, flexShrink: 0,
          display: 'flex', flexDirection: 'column', gap: 10,
        }}>
          <SummaryCard d={d} />
          <ActionsCard onAction={onAction} />
        </div>
      )}
    </div>
  );
}
